import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthStore } from '@app/stores/auth.store';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';

let isRefreshing = false;
let refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<
  string | null
>(null);

const addTokenHeader = (request: HttpRequest<any>, token: string) => {
  return request.clone({
    headers: request.headers.set('Authorization', `Bearer ${token}`),
  });
};

const hasSessionHint = (accessToken: string | null): boolean => {
  if (accessToken) {
    return true;
  }

  try {
    return !!(
      localStorage.getItem('accessToken') ||
      localStorage.getItem('tokenExpiresAt')
    );
  } catch {
    return false;
  }
};

const handle401Error = (
  req: HttpRequest<any>,
  next: HttpHandlerFn,
  authStore: any
): Observable<HttpEvent<any>> => {
  if (!isRefreshing) {
    isRefreshing = true;
    refreshTokenSubject.next(null);

    return authStore.handle401Error().pipe(
      switchMap((newAccessToken: string) => {
        isRefreshing = false;
        refreshTokenSubject.next(newAccessToken);
        return next(addTokenHeader(req, newAccessToken));
      }),
      catchError((err) => {
        isRefreshing = false;
        return throwError(() => err);
      })
    );
  } else {
    return refreshTokenSubject.pipe(
      filter((token) => token !== null),
      take(1),
      switchMap((jwt) => {
        if (!jwt) {
          authStore.logout();
          return throwError(() => new Error('Refresh failed while waiting.'));
        }
        return next(addTokenHeader(req, jwt));
      }),
      catchError((err) => {
        authStore.logout();
        return throwError(() => err);
      })
    );
  }
};

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authStore = inject(AuthStore);
  const accessToken = authStore.currentAccessToken();
  const shouldTryRefreshOn401 = hasSessionHint(accessToken);

  // Always include credentials so Set-Cookie / cookies work cross-origin
  let authReq = req.clone({ withCredentials: true });

  if (accessToken) {
    authReq = addTokenHeader(authReq, accessToken);
  }

  return next(authReq).pipe(
    catchError((error) => {
      if (
        error instanceof HttpErrorResponse &&
        error.status === 401 &&
        shouldTryRefreshOn401 &&
        !authReq.url.includes('/Auth/Login') &&
        !authReq.url.includes('/Auth/Refresh')
      ) {
        return handle401Error(authReq, next, authStore);
      }
      return throwError(() => error);
    })
  );
};
